import { isDefined } from "@clipboard-health/util-ts";
import { useWorkerPaySurveyFlag } from "@src/appV2/FeatureFlags/useWorkerPaySurveyFlag";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useEffect, useRef } from "react";

import type { OpenShiftListItem } from "../../../Shift/Open/types";
import { useGetWorkerPaySurveys } from "../../../WorkerPaySurvey/api/useGetWorkerPaySurveys";
import { createShiftSignature, getShiftStart } from "./utils";

const FIXED_POSITION = 2;

function logSurveyInjection(metadata?: Record<string, any>): void {
  logEvent(APP_V2_APP_EVENTS.WORKER_PAY_SURVEY_INJECTED_INTO_SHIFTS, {
    timestamp: new Date().toISOString(),
    ...metadata,
  });
}

function injectSurvey(
  shifts: OpenShiftListItem[],
  position: number,
  surveyId: string
): OpenShiftListItem[] {
  const shiftAtPosition = shifts[position - 1];
  const startTime = isDefined(shiftAtPosition) ? getShiftStart(shiftAtPosition) : undefined;

  const result: OpenShiftListItem[] = [...shifts];
  result.splice(position, 0, {
    type: "worker-pay-survey",
    start: startTime ?? new Date().toISOString(),
    surveyId,
  } satisfies OpenShiftListItem);

  logSurveyInjection({
    shift_position: position,
    shifts_count: shifts.length,
    survey_id: surveyId,
  });

  return result;
}

/**
 * Injects a worker pay survey card into the shift list.
 * - If there are 2+ shifts: after the 2nd shift
 * - If there are fewer shifts: after the last shift
 */
export function useWorkerPaySurveyInjector(shifts: OpenShiftListItem[]): OpenShiftListItem[] {
  const { variation } = useWorkerPaySurveyFlag();
  const { data: surveysData } = useGetWorkerPaySurveys({
    enabled: variation === "on",
  });
  const workerPaySurvey = surveysData?.data[0];
  const shouldShow = variation === "on" && isDefined(workerPaySurvey);

  const lastResultWithSurveyRef = useRef<OpenShiftListItem[] | undefined>(undefined);
  const lastShiftSignatureRef = useRef<string>("");

  const currentShiftSignature = shouldShow ? createShiftSignature(shifts) : "";

  const isCachedResult =
    shouldShow &&
    lastShiftSignatureRef.current === currentShiftSignature &&
    lastResultWithSurveyRef.current !== undefined;

  const shouldInjectNew = shouldShow && !isCachedResult;

  useEffect(() => {
    if (shouldInjectNew) {
      logEvent(APP_V2_APP_EVENTS.WORKER_PAY_SURVEY_CARD_SHOWN);
    }
  }, [shouldInjectNew]);

  if (!shouldShow) {
    lastResultWithSurveyRef.current = undefined;
    lastShiftSignatureRef.current = "";
    return shifts;
  }

  if (isCachedResult) {
    return lastResultWithSurveyRef.current!;
  }

  const injectionPosition = Math.min(FIXED_POSITION, shifts.length);
  const result = injectSurvey(shifts, injectionPosition, workerPaySurvey.id);

  lastResultWithSurveyRef.current = result;
  lastShiftSignatureRef.current = currentShiftSignature;

  return result;
}
